import { useCallback, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { feedbackGroups } from '../../store/feedback-local-preview';
import { showsFeedbackInbox } from '../../services/feedback-api';
import { FeedbackPanel } from './FeedbackPanel';
import { LocalPreviewBar } from './LocalPreviewBar';

function MessageSquareIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

/**
 * HQ Feedback entry point. It sits in the top bar next to the notifications bell. The shell is persistent, so opening Feedback never
 * remounts the page underneath.
 *
 * Renders nothing for a signed-out visitor. Every signed-in HQ person can send feedback; only the HQ Owner also gets the Inbox tab
 * (a UI hint from the same Cognito `owner` group the server checks on every inbox call).
 */
export function FeedbackButton({ variant }: { variant: 'header' | 'floating' }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const openerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    // Hand focus back to whatever opened the panel.
    setTimeout(() => openerRef.current?.focus(), 0);
  }, []);

  if (!user) return null;

  const className = variant === 'header'
    ? 'inline-flex items-center gap-1.5 px-2 py-1.5 text-xs rounded-md text-gray-300 hover:bg-white/10 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-[#4AABDE]'
    : 'fixed bottom-4 right-4 z-[55] inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-full shadow-lg bg-[#12223f] text-white hover:bg-[#1a2b3d]';

  return (
    <>
      <button
        ref={openerRef}
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        className={className}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid="feedback-button"
        title="Send feedback"
      >
        <MessageSquareIcon className="w-5 h-5" />
        <span className="hidden sm:inline">Feedback</span>
      </button>
      {open && <FeedbackPanel onClose={close} showInbox={showsFeedbackInbox(feedbackGroups(user.groups))} />}
      {import.meta.env.DEV && import.meta.env.VITE_LOCAL_MOCK_AUTH === 'true' && <LocalPreviewBar />}
    </>
  );
}
